import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import type { ChatContext } from './chat-context-types';

// Feature-provided renderers for the chat composer's slots. Features register
// these at the app composition root (see app/chat-slot-registry.ts), so the
// chat module never imports feature code — that import direction would create
// a module cycle (features import the chat barrel for tile chrome).
export type ChatSlotRenderers = {
    /** Live cards of the feature's entities the agent may show in its reply, such as a job role from the board. */
    readonly entityCards?: readonly ChatEntityCardDefinition[];
    readonly idleSlot?: (context: ChatContext) => ReactNode;
    readonly prompt?: (context: ChatContext) => ReactNode;
};

/** What the agent learns about an entity card: its name, when to use it and the ids that locate the entity. */
export type ChatEntityCardType = {
    /** For the agent: what the card shows and where its ids come from. */
    readonly description: string;
    /** Id names, all required, e.g. architectureId and roleId. */
    readonly idNames: readonly string[];
    /** Kebab-case entity name the agent picks, e.g. job-role. */
    readonly type: string;
};

/** An entity card a feature offers: the agent names the type and its ids, the feature renders its own live card. */
export type ChatEntityCardDefinition = ChatEntityCardType & {
    /** The card, or null when the ids name nothing the user can see. */
    readonly render: (ids: Readonly<Record<string, string>>, children: ReactNode) => ReactNode;
};

/** Ties the id names to the ids the card renders with, so each id is named once. */
export const defineChatEntityCard = <TIdName extends string>(
    card: ChatEntityCardType & {
        readonly idNames: readonly TIdName[];
        readonly render: (ids: Readonly<Record<TIdName, string>>, children: ReactNode) => ReactNode;
    },
): ChatEntityCardDefinition => card;

const ChatSlotRenderersContext = createContext<readonly ChatSlotRenderers[]>([]);

export const ChatSlotRenderersProvider: FunctionComponent<
    PropsWithChildren<{ readonly renderers: readonly ChatSlotRenderers[] }>
> = ({ children, renderers }) => (
    <ChatSlotRenderersContext.Provider value={renderers}>{children}</ChatSlotRenderersContext.Provider>
);

export const useChatSlotContent = (slot: 'idleSlot' | 'prompt', context: ChatContext | null): ReactNode => {
    const renderers = useContext(ChatSlotRenderersContext);

    if (context === null) {
        return null;
    }

    for (const renderer of renderers) {
        const content = renderer[slot]?.(context);

        if (content !== undefined && content !== null) {
            return content;
        }
    }

    return null;
};

const findEntityCard = (renderers: readonly ChatSlotRenderers[], type: string): ChatEntityCardDefinition | undefined =>
    renderers.flatMap((renderer) => renderer.entityCards ?? []).find((definition) => definition.type === type);

/** The entity cards every registered feature offers, without their renderers, for the agent's tool. */
export const useChatEntityCardTypes = (): readonly ChatEntityCardType[] => {
    const renderers = useContext(ChatSlotRenderersContext);

    return useMemo(
        () =>
            renderers.flatMap((renderer) =>
                (renderer.entityCards ?? []).map(({ description, idNames, type }) => ({ description, idNames, type })),
            ),
        [renderers],
    );
};

/** The feature's card for the entity, or null when no feature knows the type or the ids miss one it needs. */
export const useChatEntityCard = (
    type: string,
    ids: Readonly<Record<string, string>>,
    children: ReactNode,
): ReactNode => {
    const definition = findEntityCard(useContext(ChatSlotRenderersContext), type);

    if (definition === undefined || definition.idNames.some((name) => !ids[name])) {
        return null;
    }

    return definition.render(ids, children);
};
